import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface ContractVariable {
  name: string;
  label: string;
  description: string;
}

const contractVariables: ContractVariable[] = [
  { name: 'nome_completo', label: 'Nome Completo', description: 'Nome completo do funcionário' },
  { name: 'nome_social', label: 'Nome Social', description: 'Nome social do funcionário' },
  { name: 'cpf', label: 'CPF', description: 'Número do CPF' },
  { name: 'rg', label: 'RG', description: 'Número do RG' },
  { name: 'ctps', label: 'CTPS (Curto)', description: 'Carteira de Trabalho (formato curto)' },
  { name: 'carteira_trabalho', label: 'CTPS (Completo)', description: 'Carteira de Trabalho e Previdência Social' },
  { name: 'pis', label: 'PIS (Curto)', description: 'Número do PIS/PASEP (formato curto)' },
  { name: 'numero_pis', label: 'PIS (Completo)', description: 'Número do PIS/PASEP (formato completo)' },
  { name: 'cargo', label: 'Cargo', description: 'Cargo/função do funcionário' },
  { name: 'salario', label: 'Salário', description: 'Valor do salário' },
  { name: 'departamento', label: 'Departamento', description: 'Departamento do funcionário' },
  { name: 'data_inicio', label: 'Data de Início', description: 'Data de início do contrato (formato: DD/MM/AAAA)' },
  { name: 'dataInicio', label: 'Data Início (Alt)', description: 'Data de início alternativa (formato: DD/MM/AAAA)' },
  { name: 'data_inicio_extenso', label: 'Data de Início (Por Extenso)', description: 'Data de início formatada: DD de MÊS de AAAA' },
  { name: 'dia_inicio', label: 'Dia de Início', description: 'Dia de início (numérico)' },
  { name: 'mes_inicio', label: 'Mês de Início', description: 'Mês de início (por extenso)' },
  { name: 'ano_inicio', label: 'Ano de Início', description: 'Ano de início (numérico)' },
  { name: 'horario_entrada', label: 'Horário de Entrada', description: 'Horário de entrada do funcionário' },
  { name: 'horario_saida', label: 'Horário de Saída', description: 'Horário de saída do funcionário' },
  { name: 'estado_civil', label: 'Estado Civil', description: 'Estado civil do funcionário' },
  { name: 'nacionalidade', label: 'Nacionalidade', description: 'Nacionalidade do funcionário' },
  { name: 'logradouro', label: 'Logradouro', description: 'Endereço/logradouro do funcionário' },
  { name: 'numero', label: 'Número', description: 'Número do endereço' },
  { name: 'bairro', label: 'Bairro', description: 'Bairro de residência' },
  { name: 'cidade', label: 'Cidade', description: 'Cidade de residência' },
  { name: 'estado', label: 'Estado', description: 'Estado de residência' },
  { name: 'cep', label: 'CEP', description: 'Código de Endereçamento Postal' },
  { name: 'cor_pele', label: 'Cor da Pele', description: 'Cor da pele do funcionário' },
  { name: 'prazo', label: 'Prazo', description: 'Prazo do contrato' },
  { name: 'razao_social', label: 'Razão Social', description: 'Razão social da empresa' },
  { name: 'cnpj', label: 'CNPJ', description: 'CNPJ da empresa' },
  { name: 'dia', label: 'Dia', description: 'Dia atual (numérico)' },
  { name: 'mes', label: 'Mês', description: 'Mês atual (por extenso)' },
  { name: 'ano', label: 'Ano', description: 'Ano atual (numérico)' },
  { name: 'data', label: 'Data Completa', description: 'Data completa atual formatada' }
];

interface ContractVariablesProps {
  // Removemos a prop onVariableSelect para focar apenas no drag-and-drop
}

export default function ContractVariables({ }: ContractVariablesProps) {
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, variable: ContractVariable) => {
    e.dataTransfer.setData('text/plain', `@${variable.name}@`);
    e.dataTransfer.effectAllowed = 'copy';
    
    // Adiciona uma classe visual para indicar que está sendo arrastado
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    // Remove o efeito visual
    e.currentTarget.style.opacity = '1';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Variáveis de Template</CardTitle>
        <CardDescription>
          Arraste as variáveis para o editor de texto na posição desejada
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2">
          {contractVariables.map((variable) => (
            <div
              key={variable.name}
              draggable
              onDragStart={(e) => handleDragStart(e, variable)}
              onDragEnd={handleDragEnd}
              className="cursor-grab active:cursor-grabbing hover:bg-accent/50 p-3 rounded-md border border-border transition-all duration-200 hover:border-primary/50 hover:shadow-sm"
              title={variable.description}
            >
              <Badge variant="outline" className="w-full justify-center text-xs font-medium">
                @{variable.name}@
              </Badge>
              <p className="text-xs text-muted-foreground mt-2 text-center font-medium">
                {variable.label}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 bg-muted/50 rounded-md border border-dashed border-muted-foreground/30">
          <p className="text-xs text-muted-foreground">
            🎯 <strong>Arraste e solte:</strong> Segure e arraste qualquer variável para a posição exata onde deseja inseri-la no texto do contrato.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}